/* eslint-disable */
/**
 * TODO - preamble
 */
import React from "react";
import { FormattedHTMLMessage } from "react-intl";
import { produce } from "immer";

import {
  VerificationService,
  StudentPersonalInfoViewModel,
  Organization,
  StudentPersonalInfoResponse,
  CustomFormFieldConfig,
} from "../../lib/types/types";

import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { hasFailedInstantMatch } from "../../lib/VerificationService/VerificationServiceHelpers";
import {
  getFieldConfigByFieldId,
  handleEmailOnKeyDown,
  updateFieldValidationErrorsByFieldId,
  updateViewModelOrganization,
} from "../../lib/utils/stepComponentHelpers/stepComponentHelpers";

import { ChangeLocaleV2Component } from "../FormFields/ChangeLocale/ChangeLocaleComponent";
import { MarketConsentWrapperComponent as MarketConsentWrapper } from "../FormFields/MarketConsentWrapper/MarketConsentWrapperComponent";
import { PhoneNumberComponent as PhoneNumber } from "../FormFields/PhoneNumber/PhoneNumberComponent";
import { CollegeNameComponent as CollegeName } from "../FormFields/CollegeName/CollegeNameComponent";
import { FirstNameComponent as FirstName } from "../FormFields/FirstName/FirstNameComponent";
import { LastNameComponent as LastName } from "../FormFields/LastName/LastNameComponent";
import { EmailComponent as Email } from "../FormFields/Email/EmailComponent";
import { BirthDateComponent as BirthDate } from "../FormFields/BirthDate/BirthDateComponent";
import { CountryComponentWrapper } from "../FormFields/Country/CountryComponentWrapper";
import { getOptions } from "../../options/options";
import { FormFieldCustom } from "../FormFields/FormFieldCustom/FormFieldCustom";
import { ExtraMessage } from "../ExtraMessage";
import { PersonalInfoWrapper } from "../PersonalInfoStepWrapper/PersonalInfoStepWrapper";
import { PersonalInfoStepSubmitButton } from "../PersonalInfoStepSubmitButton/PersonalInfoStepSubmitButton";
import { PersonalInfoHeader } from "../FormHeader/StepHeaders/PersonalInfoHeaderComponent";
import { useFlag } from "../FeatureFlags/hooks";
import { CVECNumberComponent } from "../FormFields/CVECNumber/CVECNumberComponent";

interface StepStudentPersonalInfoComponentProps {
  verificationService: VerificationService;
}

const StepStudentPersonalInfo = ({
  verificationService,
}: StepStudentPersonalInfoComponentProps) => {
  const viewModel = verificationService.viewModel as StudentPersonalInfoViewModel;
  const { fieldValidationErrors } = verificationService;
  const verificationResponse =
    verificationService.verificationResponse as StudentPersonalInfoResponse;
  const options = getOptions();

  // #region include-cvec-field-france-student
  const showCvecInputVariation = useFlag("include-cvec-field-france-student");
  const showCvecInput =
    showCvecInputVariation !== "control" &&
    showCvecInputVariation !== "default" &&
    viewModel?.countryChoice?.value === "FR";

  React.useEffect(() => {
    if (showCvecInput) {
      updateStudentViewModel("fieldsToSkipValidation", []);
    } else {
      // Don't validate CVEC number if country is not France
      updateStudentViewModel("fieldsToSkipValidation", ["cvecNumber"]);
    }
  }, [showCvecInput]);
  // #endregion include-cvec-field-france-student

  const failedInstantMatch = hasFailedInstantMatch(verificationResponse);

  const updateStudentViewModel = (key: keyof StudentPersonalInfoViewModel, value: any) => {
    const nextState: StudentPersonalInfoViewModel = produce(
      viewModel,
      (draft: StudentPersonalInfoViewModel) => {
        draft[key] = value;
      },
    );
    verificationService.updateViewModel(nextState);
  };

  const locale = viewModel.localeChoice.value;

  return (
    <PersonalInfoWrapper
      verificationService={verificationService}
      step={verificationResponse.currentStep}
    >
      <PersonalInfoHeader
        verificationService={verificationService}
        classNameIdentifier="student-personal-info"
      />
      <ChangeLocaleV2Component verificationService={verificationService} />
      <CountryComponentWrapper
        verificationService={verificationService}
        disabled={getFieldConfigByFieldId("country")?.disabled}
      />
      <CollegeName
        value={(verificationService.viewModel as StudentPersonalInfoViewModel).organization}
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.organization}
        isRequired
        disabled={getFieldConfigByFieldId("organization")?.disabled}
        onChange={(choice: Organization) => {
          updateViewModelOrganization(choice, verificationService);
          updateFieldValidationErrorsByFieldId("organization", choice, verificationService);
        }}
      />
      {showCvecInput && (
        <CVECNumberComponent
          isRequired={getFieldConfigByFieldId("cvecNumber")?.requireIfOptional}
          value={viewModel.cvecNumber}
          isErrored={!!fieldValidationErrors.cvecNumber}
          disabled={getFieldConfigByFieldId("cvecNumber")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("cvecNumber", newValue);
            updateFieldValidationErrorsByFieldId("cvecNumber", newValue, verificationService);
          }}
        />
      )}
      <div className="sid-names">
        <FirstName
          value={viewModel.firstName}
          isErrored={!!fieldValidationErrors.firstName}
          disabled={getFieldConfigByFieldId("firstName")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("firstName", newValue);
            updateFieldValidationErrorsByFieldId("firstName", newValue, verificationService);
          }}
        />

        <LastName
          value={viewModel.lastName}
          isErrored={!!fieldValidationErrors.lastName}
          disabled={getFieldConfigByFieldId("lastName")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("lastName", newValue);
            updateFieldValidationErrorsByFieldId("lastName", newValue, verificationService);
          }}
        />
      </div>
      <BirthDate
        value={viewModel.birthDate}
        errorId={fieldValidationErrors.birthDate}
        locale={locale}
        isRequired
        disabled={getFieldConfigByFieldId("birthDate")?.disabled}
        onChange={(newValue) => {
          updateStudentViewModel("birthDate", newValue);
          updateFieldValidationErrorsByFieldId("birthDate", newValue, verificationService);
        }}
      />
      <Email
        value={viewModel.email}
        isErrored={!!fieldValidationErrors.email}
        explanation={
          <FormattedHTMLMessage
            id="emailExplanation"
            defaultMessage="Personal or school email is fine"
          />
        }
        onChange={(newValue) => {
          updateStudentViewModel("email", newValue);
          updateFieldValidationErrorsByFieldId("email", newValue, verificationService);
        }}
        onKeyDown={(event) => handleEmailOnKeyDown(event)}
        disabled={getFieldConfigByFieldId("email")?.disabled}
      />
      {verificationService.programTheme.isSmsNotifierConfigured ||
      verificationService.programTheme.smsLoopEnabled ? (
        <PhoneNumber
          isRequired={!!verificationService.programTheme.smsLoopEnabled}
          value={viewModel.phoneNumber}
          isErrored={!!fieldValidationErrors.phoneNumber}
          disabled={getFieldConfigByFieldId("phoneNumber")?.disabled}
          onChange={(newValue) => {
            updateStudentViewModel("phoneNumber", newValue);
            updateFieldValidationErrorsByFieldId("phoneNumber", newValue, verificationService);
          }}
          selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
        />
      ) : null}
      {(options.customFormFields || []).map((config: CustomFormFieldConfig) => (
        <FormFieldCustom config={config} verificationService={verificationService} />
      ))}{" "}
      <MarketConsentWrapper
        verificationService={verificationService}
        isErrored={!!fieldValidationErrors.marketConsentValue}
        disabled={getFieldConfigByFieldId("marketConsentValue")?.disabled}
        onChange={(newValue) => {
          updateStudentViewModel("metadata", {
            ...verificationService.viewModel.metadata,
            marketConsentValue: newValue,
          });
          updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
        }}
        viewModel={viewModel}
      />
      <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />
      <PersonalInfoStepSubmitButton
        step={VerificationStepsEnum.collectStudentPersonalInfo}
        verificationService={verificationService}
        viewModel={viewModel}
        failedInstantMatch={failedInstantMatch}
        buttonText={
          <FormattedHTMLMessage
            id="step.personalInfo.submitButtonText"
            defaultMessage="Verify My Student Status"
          />
        }
      />
    </PersonalInfoWrapper>
  );
};

export const StepStudentPersonalInfoComponent = StepStudentPersonalInfo;
